import { type ReactNode, useEffect, useRef } from 'react'
import { PigmentTick } from '@/components/primitives/PigmentTick'

/** The one link style on these screens: ink, hairline underline, ironwood on hover. */
export const TAUTAN =
  'text-text underline decoration-border underline-offset-4 hover:text-secondary hover:decoration-secondary focus-settle'

/**
 * A link (or a button drawn as one) on a line of its own under a form: 44 px tall on coarse
 * pointers, as "Lupa PIN?" is. A link inside a sentence keeps `TAUTAN`.
 */
export const TAUTAN_BARIS = `${TAUTAN} inline-flex min-h-10 items-center pointer-coarse:min-h-11`

interface AuthCardProps {
  title: string
  /** Under the title in `label` muted: the person and the parish on the PIN screen. */
  subtitle?: string | undefined
  /**
   * A screen that ends a flow (the link spent, the password saved): the title takes the focus
   * when it is shown, so the page is read from its top and the focus is not left on <body>.
   */
  fokusJudul?: boolean | undefined
  children: ReactNode
}

/**
 * The body of every sign-in screen under the layout's mark: the Newsreader title with its
 * pigment-tick, then the screen's one question. No border, no shadow: paper, one field
 * group, one red button.
 */
export function AuthCard({ title, subtitle, fokusJudul = false, children }: AuthCardProps) {
  const judul = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    if (fokusJudul) judul.current?.focus()
  }, [fokusJudul])
  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1
          ref={judul}
          tabIndex={fokusJudul ? -1 : undefined}
          className="flex items-baseline gap-1 font-display text-title text-text outline-none"
        >
          <PigmentTick />
          {title}
        </h1>
        {subtitle !== undefined && <p className="text-label text-muted">{subtitle}</p>}
      </div>
      {children}
    </div>
  )
}
